import { CITY } from '@city'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, type ReactElement } from 'react'
import { Link } from 'react-router-dom'
import type { SchemaLine } from '../../schema/types'
import { lineHexForTheme } from '../../schema/lineColor'
import { useStationPageTheme } from './useStationPageTheme'
import { fetchLineStatuses, type LineStatusEntry } from './fetchLineStatuses'
import { lineStatusTone } from './lineStatusTone'
import { LineRoundelGlyph, ChevronRightGlyph } from './StationPageGlyphs'
import styles from './StationLinesSection.module.css'

interface Props {
  /** Display name of the station, e.g. `Baker Street`. */
  stationName: string
  /** NAPTAN id of the station — the transfer links re-key the page by it. */
  naptanId: string
  /** URL locale segment for the transfer links. */
  locale: string
  /** Cluster lines EXCLUDING the page's current line. */
  lines: readonly SchemaLine[]
}

/** `two`, `three`… — the design spells the count ("four other Tube lines"). */
const COUNT_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
]

/**
 * Overview content block — "Other lines at <station>" (Figma
 * 8597:41539, TASK-699): every cluster line except the current one as
 * a row of roundel ring (line colour), name, live TfL line status and
 * a chevron. A row is a TRANSFER: it re-opens this same station page
 * keyed to that line (`/station/<naptanId>?line-id=<lineId>`), so the
 * hero roundel, timetable record and this block itself re-derive from
 * the chosen line's cluster record.
 *
 * Status: `fetchLineStatuses` once on mount — the section is static
 * editorial context, not a live board; the Service fact above already
 * carries the 60 s live loop. Absent data reads Good service.
 *
 * A single-line station renders nothing — "other lines" with an empty
 * list is a lie.
 */
export function StationLinesSection({
  stationName,
  naptanId,
  locale,
  lines,
}: Props): ReactElement | null {
  const { t, i18n } = useTranslation('station-page')
  const theme = useStationPageTheme()
  const [statuses, setStatuses] = useState<Record<string, LineStatusEntry>>({})

  const slugs = lines
    .map((l) => l.slug)
    .filter((s): s is string => s !== undefined)
    .join(',')
  useEffect(() => {
    // No live line status in a city without TfL (NYC).
    if (slugs === '' || !CITY.lineStatus) return
    let cancelled = false
    void fetchLineStatuses(slugs.split(',')).then((s) => {
      if (!cancelled) setStatuses(s)
    })
    return () => {
      cancelled = true
    }
  }, [slugs])

  if (lines.length === 0) return null

  // en keeps the design's spelled-out count ("four"); other locales get
  // the numeral — number words don't localize mechanically.
  const countWord =
    i18n.language.split('-')[0] === 'en'
      ? (COUNT_WORDS[lines.length] ?? String(lines.length))
      : String(lines.length)

  return (
    <div className={styles.section}>
      <h2 className={styles.heading}>{t('lines_heading', { station: stationName })}</h2>
      <p className={styles.subtitle}>
        {t('lines_subtitle', { station: stationName, countWord, count: lines.length })}
      </p>
      <div className={styles.grid}>
        {lines.map((line) => {
          const entry = line.slug === undefined ? undefined : statuses[line.slug]
          const status = lineStatusTone(entry?.severity, entry?.description)
          return (
            <Link
              key={line.id}
              to={`/${locale}/station/${naptanId}?line-id=${encodeURIComponent(line.id)}`}
              className={styles.row}
            >
              <span className={styles.roundel} style={{ color: lineHexForTheme(line, theme) }}>
                <LineRoundelGlyph size={33} />
              </span>
              <span className={styles.name}>{line.localName ?? line.id}</span>
              <span className={`${styles.status} ${styles[`status-${status.tone}`]}`}>
                {status.rawLabel ?? t(status.labelKey ?? 'line_status_good')}
              </span>
              <ChevronRightGlyph size={17} className={styles.chevron} />
            </Link>
          )
        })}
      </div>
    </div>
  )
}
